// The ThermoMaven probe in cook mode (2026-10-15, Matt: "Is there any way
// that we could get the app to read the thermometer's value directly?").
//
// While cook mode is open, the app asks the Lambda for the latest reading
// every POLL_MS (the Lambda connects to ThermoMaven's cloud and hangs up -
// see probe/thermomaven.mjs). The reading goes into the shared state, where
// the cook screen shows it and the teacher gets it with every message. Two
// moments are announced on their own, because they are the two the cook
// would otherwise have to watch for: the oven air reaching roasting heat,
// and the meat reaching its target.
//
// Unofficial protocol: when a read fails the chip says so and the cook
// reads the probe's own screen, as before.
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { readProbe } from './api.js';
import { setProbe } from '../state.js';

export const POLL_MS = 15000;
export const OVEN_READY_F = 400;

/** The one reading cook mode cares about: the first probe with a number. */
export const pickReading = (data) => (data?.readings || []).find((r) => typeof r.meat === 'number' || typeof r.ambient === 'number') || null;

/**
 * What changed between two readings that is worth saying out loud. Pure.
 * `target` is the lesson's done temperature when it has one, else the
 * probe's own alarm.
 */
export function probeMoments (before, now, { target = null } = {}) {
  const out = [];
  if (!now) return out;
  const goal = target || now.target || null;
  if (typeof now.ambient === 'number' && now.ambient >= OVEN_READY_F && (!before || !(before.ambient >= OVEN_READY_F))) {
    out.push({ kind: 'oven-ready', text: `The probe reads ${Math.round(now.ambient)}°F in the oven: it's at roasting heat.` });
  }
  if (goal && typeof now.meat === 'number' && now.meat >= goal && (!before || !(before.meat >= goal))) {
    out.push({ kind: 'done', text: `The probe reads ${Math.round(now.meat)}°F: that's done. Take it out.` });
  }
  return out;
}

/**
 * What the chip shows on a cook-mode phase. Pure. Prep AND steps: the probe
 * often sits in the preheating oven while the prep is still going (household,
 * 2026-10-27: "check ambient oven temp during a prep step"), which reverses
 * the steps-only rule of 2026-10-15. Never on the "On the plate" screen.
 */
export function probeChip (phase, status, reading) {
  if (phase !== 'prep' && phase !== 'steps') return { probe: null, status: '' };
  if (status === 'unavailable') return { probe: null, status: '' };
  return { probe: reading || null, status };
}

/** Polls while the calling component is mounted. `onMoment(moment)` for each announcement. */
export function useProbe ({ target = () => null, onMoment = () => {} } = {}) {
  const status = ref('reading'); // reading | live | stale | unavailable
  let timer = null;
  let last = null;
  let stopped = false;
  async function tick () {
    if (stopped) return;
    if (document.visibilityState === 'hidden') { timer = setTimeout(tick, POLL_MS); return; }
    try {
      const reading = pickReading(await readProbe());
      if (reading) {
        const now = { ...reading, readAt: Date.now() };
        for (const m of probeMoments(last, now, { target: target() })) onMoment(m);
        last = now;
        setProbe(now);
        status.value = 'live';
      }
    } catch (error) {
      if (/No probe|not set up/i.test(error.message)) { status.value = 'unavailable'; setProbe(null); return; }
      status.value = 'stale';
    }
    timer = setTimeout(tick, POLL_MS);
  }
  onMounted(tick);
  onBeforeUnmount(() => { stopped = true; clearTimeout(timer); setProbe(null); });
  return { status };
}
